import { useFetchSkills } from '../../../hooks/useFetchSkills';
import { useTransaction } from '../../../context/TransactionContext';
import { useTranslation } from 'react-i18next';
import { usePage } from '../../../context/PageContext';
import { useLocation } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { ISkill, ITransaction } from '../../../interface/transaction';
import AddEditTransactionHeader from './addEditTransactionHeader';
import NoData from '../noData';
import { SkillEmpty, Slash } from '../../icons';
import SearchInputWithFilters from '../SearchInputWithFilters';
import { Radio, RadioGroup, Spin, Text } from '../../Ant';
import { useTimer } from '../../../context/TimerContext';
import { ITimer } from '../../../interface/timer';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { useEscapeToGoBack } from '../../../hooks/useEscapeToGoBack';
import SortSelector from '../../sortPopover/sortSelector';
import { ISortOption, IActiveSortState } from '../../../interface/common';
import SkeletonOptionList from '../../common/skeleton/skeletonOptionList';
import CollapsibleListLayout from '../../collapsibleListLayout/collapsibleListLayout';
import { useQueryClient } from '@tanstack/react-query';
import { useDiscardDialog } from '../../../hooks/useDiscardDialog';
import { isTransactionFormChanged } from './isTransactionFormChanged';
import { isTimerFormChanged } from '../../timer/isTimerFormChanged';
import CustomModal from '../../informationPopup/customModal';

const AddSkill = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const location = useLocation();
  const { transaction, updateTransaction, initialTransaction } = useTransaction();
  const { timer, updateTimer, initialTimer } = useTimer();
  const { setTitle } = usePage();
  const { goBack, goBackWithSelection } = useNavigationStack();
  const isTimerRoute = location?.state?.isFromTimer ?? false;
  const { showDiscardDialog, promptDiscardIfChanged, handleDiscardConfirm, handleDiscardCancel } =
    useDiscardDialog();
  const [searchValue, setSearchValue] = useState<string>('');
  const skillSortOptions: ISortOption[] = useMemo(
    () => [
      {
        field: 'name',
        label: t('transaction:requiredSkill'),
        type: 'text',
        defaultDirection: 'ASC',
      },
    ],
    [t],
  );

  const [activeSort, setActiveSort] = useState<IActiveSortState>({
    field: 'name',
    direction: 'ASC',
  });

  const {
    data: skillList,
    isLoading: isSkillsLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useFetchSkills({
    sortBy: [`${activeSort.field}:${activeSort.direction}`],
    pageSize: 50,
    search: searchValue,
    fields: ['name'],
  });
  const refreshSkillList = () => {
    queryClient.removeQueries({ queryKey: ['skills-list'] });
    return refetch();
  };
  const handleBack = () => {
    goBack();
  };
  useEscapeToGoBack(handleBack);
  const handleTimerBack = () => {
    goBack();
  };
  const handleClose = () => {
    if (isTimerRoute) {
      promptDiscardIfChanged(isTimerFormChanged(timer, initialTimer, !!timer?.id), () => {
        updateTimer(null);
        goBack(undefined, 2);
      });
      return;
    }
    promptDiscardIfChanged(
      isTransactionFormChanged(transaction, initialTransaction, !!transaction?.id),
      () => {
        updateTransaction(null);
        goBack(undefined, 2);
      },
    );
  };
  const { ref, inView } = useInView({
    threshold: 0.5, // Trigger when 50% of the target is visible
  });
  const handleTransaction = (skill: ISkill) => {
    const payload = {
      ...transaction,
      skill: skill,
      isManualSkill: true,
      shouldLoadServerPrice: false,
    } as ITransaction;
    updateTransaction(payload);
    goBackWithSelection();
  };
  const handleTimer = (skill: ISkill) => {
    const payload = {
      ...timer,
      skillId: skill.id,
      skill: skill,
    };
    updateTimer(payload as unknown as ITimer);
    goBackWithSelection();
  };
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && skillList?.length > 0) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage]);
  useEffect(() => {
    setTitle(
      <AddEditTransactionHeader
        handleBack={isTimerRoute ? handleTimerBack : handleBack}
        handleClose={handleClose}
        showBackWithCross
        title={t('transaction:selectSkill')}
        showCross
      />,
    );
  }, []);
  const renderLoader = () => {
    if (isFetching) {
      return <Spin data-testid="small-indicator" size="small" />;
    }
    return null;
  };
  return (
    <>
      <CollapsibleListLayout
        testId="skill-selection"
        header={
          <SearchInputWithFilters
            autoFocus
            searchTitle={t('transaction:searchSkills')}
            handleSearch={(val) => {
              setSearchValue(val);
            }}
            handleRefresh={refreshSkillList}
          >
            <SortSelector
              modalLabel={t('common:sorting.title')}
              options={skillSortOptions}
              onApply={(sort) => setActiveSort(sort)}
              activeSort={activeSort}
            />
          </SearchInputWithFilters>
        }
      >
        {skillList && skillList.length > 0 && (
          <RadioGroup
            data-testid="radio-group"
            className="d-flex fd-column gap-10"
            value={transaction?.skill?.id}
          >
            {skillList.map((skill: ISkill) => (
              <Radio
                key={skill.id}
                data-testid={`skill-radio-${skill?.name}`}
                value={skill.id}
                onClick={() => {
                  const selectedItem = skillList.find((item) => item.id === skill?.id);
                  if (isTimerRoute) {
                    handleTimer(selectedItem);
                  } else {
                    handleTransaction(selectedItem);
                  }
                }}
              >
                <Text $fontsize="13px" $color="title" $weight={500} $lineheight="20px">
                  {skill.name}
                </Text>
              </Radio>
            ))}
          </RadioGroup>
        )}
        {hasNextPage && <div ref={ref} className="loading-indicator" />}
        {(!skillList || (Object.entries(skillList).length === 0 && !isSkillsLoading)) && (
          <div className="flex-grow d-flex justify-center items-center">
            <NoData
              header={t('transaction:noSkills')}
              subHeader={t('common:tryChangingSearchCriteria')}
              icon={<SkillEmpty />}
            />
          </div>
        )}
        {isSkillsLoading ? <SkeletonOptionList /> : renderLoader()}
      </CollapsibleListLayout>
      <CustomModal
        visible={showDiscardDialog}
        onConfirm={handleDiscardConfirm}
        icon={<Slash />}
        title={t('common:discardChangesText')}
        subdescription={t('common:anyChangeMadeWillBeLostAndCannotBeReverted')}
        confirmText={t('common:discard')}
        cancelText={t('common:cancel')}
        onClose={handleDiscardCancel}
      />
    </>
  );
};

export default AddSkill;
